/* eslint-disable unused-imports/no-unused-vars */
import { GenericObject } from './../../common/types';
import { random, round } from 'lodash';
import {
    AgencyProfile,
    AttributeView,
    DispatchArea,
    LinkTypesEnumType,
    LocationView,
    MapLedgerEventView,
    MapLedgerUnitView,
    MapViewportEventView,
    MapViewportUnitView,
    PhoneCallDto,
    StationView,
    SummaryCategoryEnumType,
    UnitStateGroupEnumType,
    AmlLocation,
    AmlPhoneCallDto,
} from '@mark43/cad-api';
import { AgencyTypeGlobal, globalAttributes } from '../../common/global-attributes';
import type { TimestampedGeoPoint, UnitClass } from '../../esri/types';
import type { GeoPoint } from '../../location/types';

const EVENT_ID_BASE = 10000;
const UNIT_ID_BASE = 50000;
const USER_ID_BASE = 90000;
const INCREMENTAL = 1000;

// event 10001 has assigned unit 50001 and 50002
// event 10002 has assigned unit 50003 and 50004
// (select event 10001 or 10002 will show EVENT_TO_UNIT polyline)
const EVENTS_ASSIGNED_UNITS = {
    [EVENT_ID_BASE + 1]: [UNIT_ID_BASE + 1, UNIT_ID_BASE + 2],
    [EVENT_ID_BASE + 2]: [UNIT_ID_BASE + 3, UNIT_ID_BASE + 4],
};
const UNITS_ASSIGNED_EVENTS = {
    [UNIT_ID_BASE + 1]: EVENT_ID_BASE + 1,
    [UNIT_ID_BASE + 2]: EVENT_ID_BASE + 1,
    [UNIT_ID_BASE + 3]: EVENT_ID_BASE + 2,
    [UNIT_ID_BASE + 4]: EVENT_ID_BASE + 2,
};

const defaultCenter = { latitude: 36.7804539, longitude: -119.8148565 };
const sw = { latitude: 40.604201, longitude: -74.10998 };
const se = { latitude: 40.622341, longitude: -73.753098 };
const ne = { latitude: 40.795605, longitude: -73.714656 };
const nw = { latitude: 40.821948, longitude: -73.95772 };

const AGENCY_CLASSES = ['Police', 'Fire', 'EMS'];
const EVENT_SUMMARY_CATEGORY = ['EVENT_ASSIGNED', 'EVENT_UNASSIGNED'];
const UNIT_SUMMARY_CATEGORY = [
    'UNIT_AVAILABLE',
    'UNIT_UNAVAILABLE',
    'UNIT_ADMIN',
    'UNIT_ASSIGNED',
    'UNIT_UNMANNED',
];
const UNIT_STATUS_GROUP = [
    'ADMIN',
    'AVAILABLE',
    'DISPATCHED',
    'EN_ROUTE',
    'PURSUIT_EN_ROUTE',
    'EN_ROUTE_ALTERNATE',
    'SPECIAL_ASSIGNMENT',
    'TRANSPORT_EN_ROUTE',
    'UNAVAILABLE',
    'UNMANNED',
    'VIRTUAL_PATROL',
];
type AGENCY_CLASS_INITIAL = 'P' | 'F' | 'E';
const AGENCY_CLASS_TO_GLOBAL_ATTR_ID: Record<AGENCY_CLASS_INITIAL, AgencyTypeGlobal> = {
    P: globalAttributes.agencyTypeGlobal.POLICE,
    F: globalAttributes.agencyTypeGlobal.FIRE,
    E: globalAttributes.agencyTypeGlobal.EMS,
};

export const generateCenter = () => {
    return defaultCenter;
};

const generateAmlLocation = ({
    amlPhoneCallId,
    id,
    latitude,
    locationTimestamp,
    longitude,
    uncertaintyRadius,
    rgAddress,
    confidencePercentage,
}: {
    amlPhoneCallId: number;
    id: number;
    latitude: number;
    longitude: number;
    locationTimestamp: number;
    uncertaintyRadius: number;
    rgAddress: string;
    confidencePercentage: number;
}): AmlLocation => {
    return {
        amlPhoneCallId,
        createdDateUtc: new Date(locationTimestamp).toUTCString(),
        departmentId: 1,
        id,
        latitude,
        locationTimestamp: 1,
        longitude,
        uncertaintyRadius,
        confidencePercentage,
        rgAddress,
    };
};
export const generatePhoneCall = (): PhoneCallDto => {
    return {
        id: 31460877457,
        labelPrefix: '<02>',
        rmsEventId: 31460877457,
        createdDateUtc: '2022-02-02T16:03:37.000Z',
        stationId: 12753108376,
        phoneNumberId: 55289481725,
        reportingParty: 'Test, Test',
        phoneNumberRaw: '+15555555555',
        phoneNumberDisplay: '+1 555-555-5555',
        address: '',
        callerGps: '37.040201, -119.51507',
    };
};
export const generateAmlCall = (amlCenter = defaultCenter): AmlPhoneCallDto => {
    const numberOfLocations = 10;

    return {
        id: 2518,
        departmentId: 26746111,
        createdDateUtc: '2024-04-16T15:25:33.000Z',
        amlSource: 'RAPIDSOS',
        phoneNumber: '15555555555',
        phoneNumberDisplay: '(555) 555-5555',
        phoneNumberId: 55289481725, // TODO: MAP remove
        locations: new Array(numberOfLocations).fill(0).map((_, i) => {
            return generateAmlLocation({
                amlPhoneCallId: 2518,
                id: i + 1,
                latitude: amlCenter.latitude + 0.002 * (i + 1),
                longitude: amlCenter.longitude + 0.001 * i * (i + 1),
                locationTimestamp: 1712780975000 - (numberOfLocations - i) * 1000,
                uncertaintyRadius: 250,
                confidencePercentage: 90,
                rgAddress: 'Test Address',
            });
        }),
        additionalData: {
            id: 1410,
            departmentId: 26746111,
            amlPhoneCallId: 2518,
            payload:
                '{"emergency_health_association": {"data": {"profile": {"uri": "tel://15555555555", "parent_uri": "tel://15555555555", "phone_number": "+15555555555", "first_name": "Jsiwpbmu", "last_name": "Aktclpaw", "provider_name": "EmergencyProfile.org", "gender": "Female", "date_of_birth": 24408000000, "age": 53, "home_address": {"street1": "111 Home St", "street2": "Apt 2", "city": "Elbow", "state": "MD", "zip_code": "22222"}, "medical_fields": [{"description": "Has experienced one or more symptoms associated with COVID-19", "timestamp_info": "Date of symptoms", "timestamp": 1585828800000}, {"description": "Has potentially been exposed to a person with a lab-confirmed COVID-19 positive person", "timestamp_info": "Date of potential exposure", "timestamp": 1586088000000}, {"description": "Has had a positive COVID 19 test", "timestamp_info": "Date positive test taken", "timestamp": 1586692800000}], "medical_other": "I hardly sleep", "preexisting_conditions": "heart arrhythmia", "allergies": "shellfish", "emergency_contacts": [{"name": "Arthur Allen", "relation": "uncle", "email": "arthur@allens.org", "phone_number": "+15555555555"}, {"name": "Betsy Barry", "relation": "aunt", "email": "betsy@aunts.org", "phone_number": "+16666666666"}], "last_updated": 1588289230000, "stored_location": {"latitude": 37.7591463, "longitude": -122.1909899, "altitude": null, "uncertainty": 2.5, "address": "", "source": "CALL"}}}}, "medicalert": {"data": null}}',
            createdDateUtc: '2024-04-16T15:25:35.000Z',
        },
        startTime: 1713267062017,
    };
};

export const nextRandomLatLng = (curLat: number, curLng: number, direction = 1) => {
    const randomNum = random(0, 0.0004);
    const randomNum2 = random(0, 0.0005);
    return {
        latitude: round(curLat + direction * randomNum, 8),
        longitude: round(curLng + direction * randomNum2, 8),
    };
};

const generateTimestampedGeoPoint = ({
    latitude,
    longitude,
    center,
}: {
    latitude?: number;
    longitude?: number;
    center?: GeoPoint;
}): TimestampedGeoPoint => {
    return {
        timestamp: new Date().getTime(),
        latitude: latitude ?? center?.latitude ?? defaultCenter.latitude,
        longitude: longitude ?? center?.longitude ?? defaultCenter.longitude,
    };
};

const generateLocationView = ({
    id,
    latitude,
    longitude,
    center,
}: {
    id: number;
    latitude?: number;
    longitude?: number;
    center?: GeoPoint;
}): LocationView => {
    return {
        id,
        departmentId: 1,
        areCoordinatesVerified: true,
        multiAgencyClassificationDtos: [],
        latitude: latitude ?? center?.latitude ?? defaultCenter?.latitude,
        longitude: longitude ?? center?.longitude ?? defaultCenter?.longitude,
        type: 'ADDRESS',
        country: 'US',
        postalCode: '100',
        adminArea1: 'CA',
        locality: 'FRESNO',
        streetAddress: `${id} FRESNO ST`,
        crossStreet1: `E ${id} AV`,
        crossStreet2: `${id + 1} ST`,
        streetAddressAlias: `${id} FRESNO ST`,
        crossStreet1Alias: `E ${id} AV`,
        crossStreet2Alias: `${id + 1} ST`,
        fullAddressKey: `${id} FRESNO ST\nFRESNO, CA 93706\nUNITED STATES`,
        displayLineFirst: `${id} FRESNO ST`,
        displayLineOptionalMiddle: `b/t E ${id} AV & ${id + 1} ST`,
        displayLineLast: 'FRESNO, CA 93706',
        displayLineCountry: 'UNITED STATES',
        resolverSource: 'STREET_CENTERLINE',
        entityLinks: [
            {
                additionalDates: [],
                createdDateUtc: '',
                entityType: 'LOCATION',
                linkType: 0 as LinkTypesEnumType,
                updatedDateUtc: '',
                createdBy: 0,
                updatedBy: 0,
                rmsEventId: 0,
                departmentId: 0,
                locationId: 0,
                entityId: 0,
                subdivision3AttrId: 28072304,
                crossStreet1: `E ${id} AV`,
                crossStreet2: `${id + 1} ST`,
                policeDispatchArea: 'A2',
                medicalDispatchArea: 'P',
                shapefileProperties: [],
                subdivisionFire1AttrId: 63320022,
                dispatchAreas: [
                    {
                        id: 77628585,
                        agencyTypeGlobalAttrId: 203003,
                        name: 'P',
                        departmentId: 26746111,
                        isOutOfJurisdiction: false,
                        status: 'ACTIVE',
                    },
                    {
                        id: 31765699,
                        agencyTypeGlobalAttrId: 203002,
                        name: 'A2',
                        departmentId: 26746111,
                        isOutOfJurisdiction: false,
                        status: 'ACTIVE',
                    },
                ],
            },
        ],
    };
};

export const generateUnit = ({
    id,
    callSign,
    summaryCategory,
    unitStateGroup,
    unitClass,
    agencyTypeGlobalAttrId,
    latitude,
    longitude,
    gpsLocation,
    geoLocation,
    center,
}: {
    id: number;
    callSign: string;
    summaryCategory: SummaryCategoryEnumType;
    unitStateGroup: UnitStateGroupEnumType;
    unitClass: UnitClass;
    agencyTypeGlobalAttrId: AgencyTypeGlobal;
    latitude?: number;
    longitude?: number;
    gpsLocation: TimestampedGeoPoint;
    geoLocation: TimestampedGeoPoint;
    center?: GeoPoint;
}): {
    mapViewportUnit: MapViewportUnitView;
    mapLedgerUnit: MapLedgerUnitView;
} => {
    const user1EntityId = id + USER_ID_BASE + INCREMENTAL * 1;
    const user2EntityId = id + USER_ID_BASE + INCREMENTAL * 2;

    const { latitude: nextLat1, longitude: nextLng1 } = nextRandomLatLng(
        latitude ?? center?.latitude ?? defaultCenter.latitude,
        longitude ?? center?.longitude ?? defaultCenter.longitude
    );
    const { latitude: nextLat2, longitude: nextLng2 } = nextRandomLatLng(
        latitude ?? center?.latitude ?? defaultCenter.latitude,
        longitude ?? center?.longitude ?? defaultCenter.longitude
    );
    const assignedEventId = UNITS_ASSIGNED_EVENTS[id];

    const mapViewportUnit: MapViewportUnitView = {
        entityId: id,
        type: 'UNIT',
        visible: true,
        callSign,
        summaryCategory,
        unitClass,
        agencyTypeGlobalAttrId,

        gpsLocation: generateTimestampedGeoPoint({
            latitude: nextLat1,
            longitude: nextLng1,
            center,
        }),
        geoLocation: generateTimestampedGeoPoint({
            latitude,
            longitude,
            center,
        }),
        isLocationPotentiallyInaccurate: id % 2 === 0,
        unitUserLocations: [
            {
                entityId: user1EntityId,
                gpsLocation: generateTimestampedGeoPoint({
                    latitude: nextLat1,
                    longitude: nextLng1,
                    center,
                }),
                isLocationPotentiallyInaccurate: false,
                userNameDisplay: `${callSign} User1`,
            },
            {
                entityId: user2EntityId,
                gpsLocation: generateTimestampedGeoPoint({
                    latitude: nextLat2,
                    longitude: nextLng2,
                    center,
                }),
                isLocationPotentiallyInaccurate: true,
                userNameDisplay: `${callSign} User2`,
            },
        ],
        assignedEventId,
        unitStateGroup: assignedEventId ? 'UNAVAILABLE' : 'AVAILABLE',
        mapUnitStatusOption: assignedEventId
            ? { value: 6, label: 'UNAVAILABLE' }
            : { value: 3, label: 'AVAILABLE' },
        unitStateDisplay: assignedEventId ? 'UNAVAILABLE' : 'AVAILABLE',
    };

    const mapLedgerUnit: MapLedgerUnitView = {
        entityId: id,
        callSign,
        summaryCategory,
        unitStateGroup,
        agencyTypeGlobalAttrId,
        geoLocation,
        primarySubdivision: 'primarySubdivision',
        dispatchArea: 'A3',
        equipment: `Equipment ${id}`,
        personnel: 'Washburn',
        placeName: `${id} Fresno St`,
        shiftNotes: 'unit shift notes',
        skills: 'unit skills',
        subdivisions: 'N100 / N01 / SE',
        tag: `Tag ${id}`,
        transportPlaceName: 'Fresno Park',
        transportStreetAddress: `${id} Park Ave`,
        unitStatusUpdatedDateUtc: `2024-01-${(id % 20) + 1}T${(id % 20) + 2}:48:08.000Z`,
        areCoordinatesVerified: true,
        displayLineFirst: `${id} Fresno St`,
        displayLineLast: '2nd FL',
        locationSource: 'MARK43',
        type: 'UNIT',
        unitAgency: 'FSO',
        unitStateDisplay: 'AVAILABLE',
        eventAreCoordinatesVerified: false,
        eventDisplayLineFirst: '',
        eventDisplayLineLast: '',
        eventLocationSource: '',
    };
    if (assignedEventId) {
        mapLedgerUnit.assignedEventId = assignedEventId;
        mapLedgerUnit.assignedAgencyEventNumber = 'MOCKED 2312120001';
        mapLedgerUnit.assignedEventPriority = `P${(id % 5) + 1}`;
        mapLedgerUnit.cfsCode = 'Caliendo';
        mapLedgerUnit.cfsDescription = '0511';
        mapLedgerUnit.eventLocationNotes = 'Something about this location';
        mapLedgerUnit.eventNarrative = 'This is happening';
        mapLedgerUnit.eventPlaceName = `1000 Central AV, Sanger, CA 123856 Building ${id}, Apartment 2`;
        mapLedgerUnit.eventDisplayLineSubPremise = `Building ${id}, Apartment 2`;
        mapLedgerUnit.eventAreCoordinatesVerified = true;
        mapLedgerUnit.eventDisplayLineFirst = '1000 Central AV';
        mapLedgerUnit.eventDisplayLineLast = `Building ${id}, Apartment 2`;
        mapLedgerUnit.eventLocationSource = 'MARK43';
        mapLedgerUnit.unitStateGroup = 'UNAVAILABLE';
        mapLedgerUnit.unitStateDisplay = 'DISPATCHED';
    } else {
        mapLedgerUnit.unitStateGroup = 'AVAILABLE';
        mapLedgerUnit.unitStateDisplay = 'AVAILABLE';
    }

    return {
        mapViewportUnit,
        mapLedgerUnit,
    };
};

export const generateUnits = (
    n: number,
    center?: GeoPoint
): { mapViewportUnits: MapViewportUnitView[]; mapLedgerUnits: MapLedgerUnitView[] } => {
    const mapViewportUnits: MapViewportUnitView[] = [];
    const mapLedgerUnits: MapLedgerUnitView[] = [];

    for (let index = 0; index < n / 2; index++) {
        const { latitude: randomLat, longitude: randomLng } = nextRandomLatLng(
            center?.latitude ?? defaultCenter.latitude,
            center?.longitude ?? defaultCenter.longitude
        );
        const randomAgencyClass = AGENCY_CLASSES[random(AGENCY_CLASSES.length - 1)];
        const agencyClass = randomAgencyClass.charAt(0);
        const randomSummaryCategory =
            agencyClass === 'P'
                ? `${UNIT_SUMMARY_CATEGORY[random(UNIT_SUMMARY_CATEGORY.length - 1)]}`
                : agencyClass === 'F'
                ? `FIRE_${UNIT_SUMMARY_CATEGORY[random(UNIT_SUMMARY_CATEGORY.length - 1)]}`
                : `EMS_${UNIT_SUMMARY_CATEGORY[random(UNIT_SUMMARY_CATEGORY.length - 1)]}`;

        const unitStateGroup = UNIT_STATUS_GROUP[
            random(UNIT_STATUS_GROUP.length - 1)
        ] as UnitStateGroupEnumType;
        const agencyTypeGlobalAttrId =
            AGENCY_CLASS_TO_GLOBAL_ATTR_ID[agencyClass as AGENCY_CLASS_INITIAL];

        const { mapViewportUnit: mapViewportUnit1, mapLedgerUnit: mapLedgerUnit1 } = generateUnit({
            id: UNIT_ID_BASE + index * 2 + 1,
            callSign: `MOCKED ${index * 2 + 1}${agencyClass}`,
            unitClass: randomAgencyClass as UnitClass,
            summaryCategory: randomSummaryCategory as SummaryCategoryEnumType,
            unitStateGroup,
            agencyTypeGlobalAttrId,
            latitude: randomLat,
            longitude: randomLng,
            gpsLocation: generateTimestampedGeoPoint({
                latitude: randomLat,
                longitude: randomLng,
                center,
            }),
            geoLocation: generateTimestampedGeoPoint({
                latitude: randomLat,
                longitude: randomLng,
                center,
            }),
            center,
        });

        mapViewportUnits.push(mapViewportUnit1);
        mapLedgerUnits.push(mapLedgerUnit1);

        const useSameLocation = index % 2 === 0;
        const { latitude: nextLat, longitude: nextLng } = nextRandomLatLng(randomLat, randomLng);
        // 50% chance to create a unit at the same location
        const { mapViewportUnit: mapViewportUnit2, mapLedgerUnit: mapLedgerUnit2 } = generateUnit({
            id: UNIT_ID_BASE + index * 2 + 2,
            callSign: `MOCKED ${index * 2 + 2}${agencyClass}`,
            unitClass: randomAgencyClass as UnitClass,
            summaryCategory: randomSummaryCategory as SummaryCategoryEnumType,
            unitStateGroup,
            agencyTypeGlobalAttrId,
            latitude: useSameLocation ? randomLat : nextLat,
            longitude: useSameLocation ? randomLng : nextLng,
            gpsLocation: generateTimestampedGeoPoint({
                latitude: useSameLocation ? randomLat : nextLat,
                longitude: useSameLocation ? randomLng : nextLng,
                center,
            }),
            geoLocation: generateTimestampedGeoPoint({
                latitude: useSameLocation ? randomLat : nextLat,
                longitude: useSameLocation ? randomLng : nextLng,
                center,
            }),
            center,
        });

        mapViewportUnits.push(mapViewportUnit2);
        mapLedgerUnits.push(mapLedgerUnit2);
    }
    return { mapViewportUnits, mapLedgerUnits };
};

export const nextPriority = () => {
    const isHighPriority = random(1, 10) >= 5;
    const priorityNumber = isHighPriority ? random(1, 4) : random(5, 9);
    return `P${priorityNumber}`;
};

const generateMapViewportEvent = ({
    id,
    summaryCategory,
    agencyTypeGlobalAttrId,
    priority,
    geoLocation,
}: {
    id: number;
    priority: string;
    summaryCategory: SummaryCategoryEnumType;
    agencyTypeGlobalAttrId: AgencyTypeGlobal;
    geoLocation: TimestampedGeoPoint;
}): MapViewportEventView => {
    return {
        entityId: id,
        visible: true,
        summaryCategory,
        agencyTypeGlobalAttrId,
        priority,
        geoLocation,
        assignedUnitIds: EVENTS_ASSIGNED_UNITS[id] ?? [],
        phoneCalls: [],
        summaryCategoryId: 1,
    };
};
const generateMapLedgerEvent = ({
    id,
    agencyTypeGlobalAttrId,
    priority,
    geoLocation,
}: {
    id: number;
    priority: string;
    agencyTypeGlobalAttrId: AgencyTypeGlobal;
    geoLocation: TimestampedGeoPoint;
}): MapLedgerEventView => {
    return {
        agencyEventMaterializedCautions: [],
        agencyEventNumber: `#MOCKED${id + 1}`,
        agencyName: 'FSO',
        agencyTypeGlobalAttrId,
        areCoordinatesVerified: true,
        assignedUnits: [],
        cfsCode: 'V',
        cfsDescription: 'Vehicle Stop',
        dispatchArea: 'A1',
        displayLineFirst: '123 Main St',
        displayLineLast: 'Anytown, CA 92221',
        entityId: id,
        eventLocationNotes:
            'Very long notes......... #1234567890 Very long notes......... #1234567890',
        geoLocation,
        highestPriorityLocationCaution: 1,
        isNearbyLocationCautionPriority1: true,
        isPriority1: true,
        locationSource: '',
        narrative: 'initial narrative',
        placeName: 'NYC',
        priority,
        subdivisions: '3rd FL',
        phoneCalls: [
            {
                id: 57130228886,
                createdDateUtc: '2024-01-17T18:41:04.000Z',
                phoneNumberDisplay: '234-567-8901',
                classOfService: 'Test CoS',
                callerName: 'Jon Doe',
                callerAddress: '123 Main St.',
            },
        ],
    };
};

export const generateEvents = (
    n: number,
    center?: GeoPoint
): { mapViewportEvents: MapViewportEventView[]; mapLedgerEvents: MapLedgerEventView[] } => {
    const mapViewportEvents: MapViewportEventView[] = [];
    const mapLedgerEvents: MapLedgerEventView[] = [];

    for (let index = 0; index < n / 2; index++) {
        const randomNum = random(0, 0.02);
        const randomNum2 = random(0, 0.025);
        const direction = random(1, 10) >= 5 ? 1 : -1;
        const direction2 = random(1, 10) >= 5 ? 1 : -1;
        const randomLat = (center?.latitude ?? defaultCenter.latitude) + direction * randomNum;
        const randomLng = (center?.longitude ?? defaultCenter.longitude) + direction2 * randomNum2;
        const randomAgencyClass = AGENCY_CLASSES[random(AGENCY_CLASSES.length - 1)];
        const agencyClass = randomAgencyClass.charAt(0);
        const randomSummaryCategory =
            agencyClass === 'P'
                ? `${EVENT_SUMMARY_CATEGORY[random(EVENT_SUMMARY_CATEGORY.length - 1)]}`
                : agencyClass === 'F'
                ? `FIRE_${EVENT_SUMMARY_CATEGORY[random(EVENT_SUMMARY_CATEGORY.length - 1)]}`
                : `EMS_${EVENT_SUMMARY_CATEGORY[random(EVENT_SUMMARY_CATEGORY.length - 1)]}`;
        const randomPriority1 = nextPriority();
        const randomPriority2 = nextPriority();

        const event1Id = EVENT_ID_BASE + index * 2 + 1;
        const event1GeoLocation = generateTimestampedGeoPoint({
            latitude: randomLat,
            longitude: randomLng,
            center,
        });
        const mockedMapViewportEvent1 = generateMapViewportEvent({
            id: event1Id,
            priority: randomPriority1,
            summaryCategory: randomSummaryCategory as SummaryCategoryEnumType,
            agencyTypeGlobalAttrId:
                AGENCY_CLASS_TO_GLOBAL_ATTR_ID[randomSummaryCategory as AGENCY_CLASS_INITIAL],
            geoLocation: event1GeoLocation,
        });
        const mockedMapLedgerEvent1 = generateMapLedgerEvent({
            id: event1Id,
            priority: randomPriority1,
            agencyTypeGlobalAttrId:
                AGENCY_CLASS_TO_GLOBAL_ATTR_ID[randomSummaryCategory as AGENCY_CLASS_INITIAL],
            geoLocation: event1GeoLocation,
        });
        mapViewportEvents.push(mockedMapViewportEvent1);
        mapLedgerEvents.push(mockedMapLedgerEvent1);
        const useSameLocation = index % 2 === 0;
        const { latitude: nextLat, longitude: nextLng } = nextRandomLatLng(randomLat, randomLng);
        const eventId2 = EVENT_ID_BASE + index * 2 + 2;
        const event2GeoLocation = generateTimestampedGeoPoint({
            latitude: useSameLocation ? randomLat : nextLat,
            longitude: useSameLocation ? randomLng : nextLng,
            center,
        });
        // 50% chance to create an event at the same location
        const mockedMapViewportEvent2 = generateMapViewportEvent({
            id: eventId2,
            priority: randomPriority2,
            summaryCategory: randomSummaryCategory as SummaryCategoryEnumType,
            agencyTypeGlobalAttrId:
                AGENCY_CLASS_TO_GLOBAL_ATTR_ID[randomSummaryCategory as AGENCY_CLASS_INITIAL],
            geoLocation: event2GeoLocation,
        });
        const mockedMapLedgerEvent2 = generateMapLedgerEvent({
            id: eventId2,
            priority: randomPriority1,
            agencyTypeGlobalAttrId:
                AGENCY_CLASS_TO_GLOBAL_ATTR_ID[randomSummaryCategory as AGENCY_CLASS_INITIAL],
            geoLocation: event2GeoLocation,
        });
        mapViewportEvents.push(mockedMapViewportEvent2);
        mapLedgerEvents.push(mockedMapLedgerEvent2);
    }
    return { mapViewportEvents, mapLedgerEvents };
};

export const generateStations = (n: number, center?: GeoPoint) => {
    const stations: StationView[] = [];
    for (let index = 0; index < n; index++) {
        const randomNum = random(0, 0.02);
        const randomNum2 = random(0, 0.025);
        const direction = random(1, 10) >= 5 ? 1 : -1;
        const direction2 = random(1, 10) >= 5 ? 1 : -1;
        const randomLat = (center?.latitude ?? defaultCenter.latitude) + direction * randomNum;
        const randomLng = (center?.longitude ?? defaultCenter.longitude) + direction2 * randomNum2;
        const randomAgencyType =
            index % 2 === 0
                ? globalAttributes.agencyTypeGlobal.FIRE
                : globalAttributes.agencyTypeGlobal.EMS;

        stations.push({
            id: index + 1,
            location: generateLocationView({
                id: index + 1,
                latitude: randomLat,
                longitude: randomLng,
            }),
            station: {
                agencyTypeGlobalAttrId: randomAgencyType,
                createdBy: 1,
                createdDateUtc: '',
                departmentId: 1,
                dispatchAreaId: 1,
                id: index + 1,
                isActive: true,
                name: `Station ${index + 1}`,
                rmsEventId: 2,
                updatedBy: 2,
                updatedDateUtc: '',
            },
        });
    }
    return stations;
};

export const attributesMockData: GenericObject<AttributeView> = {
    12804186513: {
        abbr: 'DANG',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 12804186513,
        inAppDescription: '',
        none: false,
        other: false,
        parentId: 12804184409,
        parentType: 'LOCATION_CAUTION_CATEGORY',
        rmsEventId: 12804186513,
        start: '2020-04-03',
        type: 'LOCATION_CAUTION',
        val: 'DANGEROUS',
    },
    12804184409: {
        abbr: 'PWGS',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 12804184409,
        inAppDescription: '',
        none: false,
        other: false,
        parentId: 222000,
        parentType: 'LOCATION_CAUTION_PRIORITY',
        rmsEventId: 12804184409,
        start: '2020-04-03',
        type: 'LOCATION_CAUTION_CATEGORY',
        val: 'Police Warning',
    },
    222000: {
        abbr: 'P1',
        def: false,
        enabled: true,
        id: 222000,
        none: false,
        other: false,
        rmsEventId: 259300176,
        start: '2017-06-13',
        type: 'LOCATION_CAUTION_PRIORITY',
        val: 'Priority 1',
    },
    12804185491: {
        abbr: 'BRO',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 12804185491,
        inAppDescription: '',
        none: false,
        other: false,
        parentId: 5537637260,
        parentType: 'LOCATION_CAUTION_CATEGORY',
        rmsEventId: 12804185491,
        start: '2020-04-03',
        type: 'LOCATION_CAUTION',
        val: 'Bromine',
    },
    5537637260: {
        abbr: 'HAZ',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5537637260,
        none: false,
        other: false,
        parentId: 222001,
        parentType: 'LOCATION_CAUTION_PRIORITY',
        rmsEventId: 12804183141,
        start: '2018-08-13',
        type: 'LOCATION_CAUTION_CATEGORY',
        val: 'Hazardous Chemicals',
    },
    222001: {
        abbr: 'P2',
        def: false,
        enabled: true,
        id: 222001,
        none: false,
        other: false,
        rmsEventId: 259300177,
        start: '2017-06-13',
        type: 'LOCATION_CAUTION_PRIORITY',
        val: 'Priority 2',
    },
    12804186001: {
        abbr: 'CHL',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 12804186001,
        inAppDescription: '',
        none: false,
        other: false,
        parentId: 5537637260,
        parentType: 'LOCATION_CAUTION_CATEGORY',
        rmsEventId: 12804186001,
        start: '2020-04-03',
        type: 'LOCATION_CAUTION',
        val: 'CHLORIDE',
    },
    12804187592: {
        abbr: 'FUM',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 12804187592,
        inAppDescription: '',
        none: false,
        other: false,
        parentId: 12804184409,
        parentType: 'LOCATION_CAUTION_CATEGORY',
        rmsEventId: 12804187592,
        start: '2020-04-03',
        type: 'LOCATION_CAUTION',
        val: 'FUMIGATION',
    },
    5537634963: {
        abbr: 'B',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5537634963,
        inAppDescription: '',
        none: false,
        other: false,
        parentId: 5537630661,
        parentType: 'LOCATION_CAUTION_CATEGORY',
        rmsEventId: 5537639877,
        start: '2018-11-29',
        type: 'LOCATION_CAUTION',
        val: 'Business',
    },
    5537634041: {
        abbr: 'Other',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5537634041,
        none: false,
        other: false,
        parentId: 5537631570,
        parentType: 'LOCATION_CAUTION_CATEGORY',
        rmsEventId: 12804188500,
        start: '2018-08-13',
        type: 'LOCATION_CAUTION',
        val: 'Other',
    },
    5537630661: {
        abbr: 'GC',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5537630661,
        none: false,
        other: false,
        parentId: 222003,
        parentType: 'LOCATION_CAUTION_PRIORITY',
        rmsEventId: 12804183686,
        start: '2018-08-13',
        type: 'LOCATION_CAUTION_CATEGORY',
        val: 'Gate Code',
    },
    222003: {
        abbr: 'P4',
        def: false,
        enabled: true,
        id: 222003,
        none: false,
        other: false,
        rmsEventId: 259300179,
        start: '2017-06-13',
        type: 'LOCATION_CAUTION_PRIORITY',
        val: 'Priority 4',
    },
    5537631570: {
        abbr: 'EMS',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5537631570,
        none: false,
        other: false,
        parentId: 222000,
        parentType: 'LOCATION_CAUTION_PRIORITY',
        rmsEventId: 12804183446,
        start: '2018-08-13',
        type: 'LOCATION_CAUTION_CATEGORY',
        val: 'EMS Warning',
    },
    55169799671: {
        abbr: 'SO',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 55169799671,
        none: false,
        other: false,
        rmsEventId: 55169799671,
        start: '2024-04-05',
        type: 'SUBDIVISION_FIRE_DEPTH_1',
        val: 'SO',
    },
    55169799553: {
        abbr: '1',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 55169799553,
        none: false,
        other: false,
        rmsEventId: 55169799553,
        start: '2024-04-05',
        type: 'SUBDIVISION_FIRE_DEPTH_2',
        val: '1',
    },
    55169799760: {
        abbr: '4',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 55169799760,
        none: false,
        other: false,
        rmsEventId: 55169799760,
        start: '2024-04-05',
        type: 'SUBDIVISION_FIRE_DEPTH_3',
        val: '4',
    },
    55169799848: {
        abbr: '2853',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 55169799848,
        none: false,
        other: false,
        rmsEventId: 55169799848,
        start: '2024-04-05',
        type: 'SUBDIVISION_DEPTH_1',
        val: '2853',
    },
    5537631994: {
        abbr: 'L11',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5537631994,
        none: false,
        other: false,
        parentId: 5537626412,
        parentType: 'SUBDIVISION_DEPTH_3',
        rmsEventId: 52813461304,
        start: '2021-01-14',
        type: 'SUBDIVISION_DEPTH_2',
        val: 'L11',
    },
    5537635263: {
        abbr: 'A2',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5537635263,
        none: false,
        other: false,
        parentId: 5537630965,
        parentType: 'SUBDIVISION_DEPTH_2',
        rmsEventId: 52813461298,
        start: '2021-01-14',
        type: 'SUBDIVISION_DEPTH_1',
        val: 'A2',
    },
    5537622364: {
        abbr: '2853',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5537622364,
        none: false,
        other: false,
        parentId: 5537631994,
        parentType: 'SUBDIVISION_DEPTH_2',
        rmsEventId: 19729400283,
        start: '2021-01-14',
        type: 'SUBDIVISION_DEPTH_3',
        val: '2853',
    },
    5569803406: {
        abbr: '1002',
        def: false,
        deptId: 5537617157,
        enabled: true,
        id: 5569803406,
        none: false,
        other: false,
        rmsEventId: 55169801118,
        start: '2024-04-05',
        type: 'SUBDIVISION_FIRE_DEPTH_1',
        val: '1002',
    },
};

const dummyAttribute = (id: number) =>
    id && {
        abbr: 'UN',
        id,
        start: '9999-12-31T00:00:00.000Z',
        val: 'Unknown attribute, please resync',
    };

export const getAttributeById = (attributes: GenericObject<AttributeView>, id: number) => {
    return attributes[id] || dummyAttribute(id);
};

export const departmentAgenciesMockData: GenericObject<AgencyProfile> = {
    5538044837: {
        agencyAbbreviation: 'SEPD',
        agencyCode: 'SEPD',
        agencyId: 5538044837,
        agencyName: 'Selma PD',
        agencyOri: 'SEPD',
        agencyTypeAttrId: 5537636149,
        agencyTypeGlobalAttrId: 203001,
        createdBy: 5537794900,
        createdDateUtc: '2019-03-05T20:19:39.000Z',
        departmentId: 5537617157,
        id: 5538044837,
        isDefault: true,
        isExternal: false,
        rmsEventId: 55169814071,
        updatedBy: 12684813498,
        updatedDateUtc: '2024-04-05T16:32:41.000Z',
    },
    5537617700: {
        agencyAbbreviation: 'FSO',
        agencyCode: 'FSO',
        agencyId: 5537617700,
        agencyName: "Fresno County Sheriff's Office",
        agencyOri: 'FSO',
        agencyTypeAttrId: 5537636149,
        agencyTypeGlobalAttrId: 203001,
        createdBy: 5537617677,
        createdDateUtc: '2019-03-05T18:33:06.000Z',
        departmentId: 5537617157,
        id: 5537617700,
        isDefault: false,
        isExternal: false,
        nibrsStartDateUtc: '2021-09-01T04:00:00.000Z',
        rmsEventId: 55169798797,
        updatedBy: 12684813498,
        updatedDateUtc: '2024-04-05T15:53:07.000Z',
    },
    5538043706: {
        agencyAbbreviation: 'FDP',
        agencyCode: 'FCDP',
        agencyId: 5538043706,
        agencyName: 'Fresno County Fire Department',
        agencyOri: 'FDP',
        agencyTypeAttrId: 5537622165,
        agencyTypeGlobalAttrId: 203002,
        createdBy: 5537794900,
        createdDateUtc: '2019-03-05T20:19:11.000Z',
        departmentId: 5537617157,
        id: 5538043706,
        isDefault: true,
        isExternal: false,
        rmsEventId: 55169814070,
        updatedBy: 12684813498,
        updatedDateUtc: '2024-04-05T16:32:41.000Z',
    },
    8342063849: {
        agencyCode: '709cd4a8-2acd-4b38-97dd-8c983e6d6551',
        agencyId: 8342063849,
        agencyName: 'Swift, Feest and Gutkowski',
        agencyTypeAttrId: 5537622165,
        agencyTypeGlobalAttrId: 203002,
        createdBy: 8341979932,
        createdDateUtc: '2019-06-21T20:09:58.000Z',
        departmentId: 5537617157,
        id: 8342063849,
        isDefault: false,
        isExternal: false,
        rmsEventId: 55169798801,
        updatedBy: 12684813498,
        updatedDateUtc: '2024-04-05T15:53:07.000Z',
    },
};

export const dispatchAreasMockData: GenericObject<DispatchArea> = {
    5569807740: {
        agencyId: 5538043706,
        agencyTypeGlobalAttrId: 203002,
        createdBy: 5537794900,
        createdDateUtc: '2019-03-07T15:30:13.000Z',
        departmentId: 5537617157,
        id: 5569807740,
        isAdminCreated: false,
        isOutOfJurisdiction: false,
        name: '1',
        radioChannelId: 26539673871,
        rmsEventId: 52813465730,
        status: 'ACTIVE',
        updatedBy: 21932098653,
        updatedDateUtc: '2023-10-27T14:37:40.000Z',
    },
    5537797613: {
        agencyId: 5537617700,
        agencyTypeGlobalAttrId: 203001,
        createdBy: 5537617677,
        createdDateUtc: '2019-03-05T18:40:55.000Z',
        departmentId: 5537617157,
        id: 5537797613,
        isAdminCreated: false,
        isOutOfJurisdiction: false,
        name: 'A2',
        radioChannelId: 26996612419,
        rmsEventId: 52841014812,
        status: 'ACTIVE',
        updatedBy: 21932098653,
        updatedDateUtc: '2023-10-30T21:15:44.000Z',
    },
};
